import React from 'react'
// components
import RowCell from '../RowCell'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// themes
import colors from 'themes/colors'
// constants
import { Modals } from 'constants/modals'
// icons
import AddCircle from '@mui/icons-material/AddCircle'
import RemoveCircle from '@mui/icons-material/RemoveCircle'
// views
import { ButtonsWrapper } from '../../views'
import { LeftRowsContainer, LeftRow, LeftRowsWrapper } from './views'
// types
import { RowCellValueChangeFn } from '../../index'
import { TMatrix, LeftSectionRow } from '../../types'
// utils
import { v4 as uuid } from 'uuid'

interface Props {
  data: LeftSectionRow[][]
  setData: React.Dispatch<React.SetStateAction<TMatrix>>
  disabled: boolean
  removeRow: () => void
  relatedDataKey: string
  onCellValueChange: RowCellValueChangeFn
}

const LeftMatrixSection: React.FC<Props> = ({
  data,
  setData,
  disabled,
  removeRow,
  relatedDataKey,
  onCellValueChange
}) => {
  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_MATRIX_ITEM_MODAL })

  const addRow = () => {
    setData((prev) => {
      const newRow = Array.from({ length: 2 }, () => ({ id: uuid(), value: null }))

      return {
        ...prev,
        bLeftSection: [...prev.bLeftSection, newRow],
        [relatedDataKey]: prev[relatedDataKey].map((row) => {
          return [...row, { id: uuid(), value: null }]
        })
      }
    })
  }

  const addColumn = () => {
    setData((prev) => {
      return {
        ...prev,
        bLeftSection: prev.bLeftSection.map((row) => [{ id: uuid(), value: null }, ...row])
      }
    })
  }

  const removeColumn = () => {
    setData((prev) => {
      return {
        ...prev,
        bLeftSection: prev.bLeftSection.map((row) => row.filter((i, idx) => idx !== 0))
      }
    })
  }

  const onColumnRemove = () => {
    showModal({
      entity: 'column',
      width: 'sm',
      handleConfirm: removeColumn
    })
  }

  return (
    <LeftRowsContainer $disabled={disabled}>
      <div style={{ display: 'flex' }}>
        {!disabled && (
          <ButtonsWrapper disabled={data[0].length === 2}>
            <RemoveCircle htmlColor={colors.blue500} onClick={onColumnRemove} />
            <AddCircle htmlColor={colors.blue500} onClick={addColumn} />
          </ButtonsWrapper>
        )}
        <LeftRowsWrapper>
          {data.map((row, idx) => (
            <LeftRow key={idx}>
              {row.map(({ value, id }, cellIdx) => (
                <RowCell
                  key={id}
                  cellId={id}
                  value={value}
                  disabled={disabled}
                  isRowHeader={cellIdx == row.length - 1}
                  onCellValueChange={onCellValueChange(idx, id)}
                />
              ))}
            </LeftRow>
          ))}
        </LeftRowsWrapper>
      </div>

      {!disabled && (
        <ButtonsWrapper disabled={data.length === 1}>
          <RemoveCircle htmlColor={colors.blue500} onClick={removeRow} />
          <AddCircle htmlColor={colors.blue500} onClick={addRow} />
        </ButtonsWrapper>
      )}
    </LeftRowsContainer>
  )
}

export default LeftMatrixSection
